import { objects } from "../utils/objects.js";
import { types } from "../utils/types.js";
import { bridge } from "../packages/bridge.js";
import { coreEngine } from "../packages/engine.js";
import { descriptors } from "../packages/descriptors.js";
import { events } from "../packages/events.js";
import { nodes } from "../packages/nodes.js";
import { time } from "../packages/time.js";
import { prepareTransform, startTransform, stopTransforms } from "../packages/transform.js";
import { createEntityLogger } from "../packages/entity-logger.js";
import { propagate, checkPendingSubscribers, unsubscribeNode } from "../packages/subscriptions.js";

const _childListeners = {};

function addChildListener(cNodeChild, parentUid) {
  if (!types.isObject(cNodeChild)) {
    return;
  }

  const childUid = cNodeChild.uid;
  const usedParentUid = parentUid ?? cNodeChild.props.parentUid;

  if (typeof childUid !== "string") {
    return;
  }

  if (typeof usedParentUid !== "string") {
    return;
  }

  _childListeners[childUid] ??= new Set();
  _childListeners[childUid].add(usedParentUid);
}

function removeChildListeners(cNodeUid) {
  delete _childListeners[cNodeUid];

  for (const childListenersSet of Object.values(_childListeners)) {
    childListenersSet.delete(cNodeUid);
  }
}

class Entity {
  props = [];
  handlers = {};
  engine = undefined;
  namespace = undefined;
  
  // Logging methods will be created in constructor

  constructor() {
    // Create logging methods with proper 'this' context
    this.#initializeLogger();

    // avoid registration of the default entity component
    const prototype = Object.getPrototypeOf(this);
    // uglifying doesn't cause any problems here
    const isSuperComponent = prototype.constructor.name === Entity.name;
    if (isSuperComponent) {
      return;
    }

    coreEngine.addImplementation(this);
  }

  #initializeLogger() {
    const logger = createEntityLogger(this);
    this.log = logger.log;
    this.debug = logger.debug;
    this.warn = logger.warn;
    this.error = logger.error;
    this.critical = logger.critical;
  }

  /**
   * Reinitialize the logger with the current namespace.
   * Used by runtime component registration where namespace is set after construction.
   */
  reinitializeLogger() {
    this.#initializeLogger();
  }

  // TODO test if this can run engine create
  // update and destroy has this test, why not init?
  init(node, message) {}

  initNode(node, event) {
    node._transforms = {};
    checkPendingSubscribers(node.uid);
    this.init(node, event);
  }

  destroy(cNode) {
    const children = nodes.getChildrenFor(cNode.uid);

    for (const child of children.values()) {
      coreEngine.destroy(child.uid);
    }
    nodes.deleteAsChild(cNode.uid);
    unsubscribeNode(cNode.uid);

    stopTransforms(cNode);

    if (!cNode.coreImplementation.engine) {
      return;
    }

    bridge.destroy({ uid: cNode.coreImplementation.engine + "/" + cNode.uid, sender: "core" });
    removeChildListeners(cNode.uid);
  }

  transform(coreNode, value) {
    const transforms = value.transforms;
    if (!transforms || transforms.length === 0) return;

    const descriptor = descriptors.get(coreNode.namespace);
    const prepared = transforms.map(entry => prepareTransform(entry, coreNode.namespace));
    for (const config of prepared) {
      const propType = descriptor?.properties[config.property]?.type;
      if (propType === "color") continue;
      startTransform(coreNode, config);
    }

    if (coreNode.coreImplementation.engine) {
      bridge.update({
        uid: coreNode.coreImplementation.engine + "/" + coreNode.uid,
        key: "transform",
        value: prepared.length === 1 ? prepared[0] : { transforms: prepared },
        sender: "core",
      });
    }
  }

  update(coreNode, config, message) {
    const { key, value, sender } = config;

    // Environment→core messages always go to receiveMessage (Branch 3)
    if (config._envMessage) {
      const messageTime = config.time ?? time.current;
      coreNode.coreImplementation.receiveMessage(coreNode, {
        uid: coreNode.uid,
        key: key || "",
        value: value || {},
        sender,
        time: messageTime,
      });

      const parentUids = _childListeners[coreNode.uid];
      if (parentUids) {
        for (const parentUid of parentUids) {
          const parentNode = nodes.get(parentUid);
          parentNode.coreImplementation.receiveMessage(parentNode, {
            uid: coreNode.uid,
            key: key || "",
            value: value || {},
            sender,
            time: messageTime,
          });
        }
      }
      return;
    }

    // Branch 1: Method call
    if (typeof coreNode.coreImplementation[key] === "function") {
      coreNode.coreImplementation[key](coreNode, value, message);
      return;
    }

    // Branch 2: Known descriptor property update
    const descriptor = descriptors.get(coreNode.namespace);
    if (descriptor?.properties[key]) {
      if (coreNode.props[key] === value || (Number.isNaN(coreNode.props[key]) && Number.isNaN(value))) {
        return;
      }

      coreNode.props[key] = value;

      if (coreNode._publishedProperties?.has(key)) {
        propagate(coreNode.uid, key);
      }

      const isRemoteOrigin = sender && sender.includes("/");

      // Remote origin: sender contains "/" — skip handlers to prevent feedback loops
      // in multi-user realtime environments
      if (typeof coreNode.coreImplementation.handlers[key] === "function" && !isRemoteOrigin && coreNode._initialized) {
        coreNode.coreImplementation.handlers[key].call(coreNode.coreImplementation, coreNode);
      }

      // Skip mirroring: environment origin, remote origin, or transform tick (environment runs its own interpolation)
      if (isRemoteOrigin || (sender && (sender === coreNode.coreImplementation.engine || sender === "transform"))) {
        return;
      }

      // should happen after the handlers in order for handlers to be able to limits incoming values (e.g. maxWidth)
      if (coreNode.coreImplementation.engine) {
        const propDef = descriptor?.properties[key];
        if (propDef?.readOnly) return;

        // reflecting property changes to the environment
        bridge.update({
          uid: coreNode.coreImplementation.engine + "/" + coreNode.uid,
          key,
          value: coreNode.props[key],
          sender: "core",
        });
      }
      return;
    }

    // Branch 3: receiveMessage — key is neither a method nor a known property
    const messageTime = config.time ?? time.current;
    coreNode.coreImplementation.receiveMessage(coreNode, {
      uid: coreNode.uid,
      key: key || "",
      value: value || {},
      sender,
      time: messageTime,
    });

    // Parent propagation
    const parentUids = _childListeners[coreNode.uid];
    if (parentUids) {
      for (const parentUid of parentUids) {
        const parentNode = nodes.get(parentUid);
        parentNode.coreImplementation.receiveMessage(parentNode, {
          uid: coreNode.uid,
          key: key || "",
          value: value || {},
          sender,
          time: messageTime,
        });
      }
    }
  }

  getInitData(node) {
    const initData = {
      uid: node.uid,
      namespace: node.coreImplementation.namespace,
    };

    //for (const key of Object.keys(node.props)) {
    //  initData[key] = objects.clone(node.props[key]);
    //}

    const descriptor = descriptors.get(node.coreImplementation.namespace);
    const props = {};
    for (const key of Object.keys(node.props)) {
      if (descriptor?.properties[key]?.readOnly) continue;
      props[key] = node.props[key];
    }
    initData.props = objects.clone(props);
    return initData;
  }

  dispatch(eventName, eventTime, eventData) {
    eventTime ??= time.current;
    eventData ??= {};

    events.addEventToQueue(eventName, eventTime, eventData);
  }

  receiveMessage() {}

  // Combines parent entity handlers with current entity, so that behavior of the parents handlers wouldn't disappear
  extendParentHandlers(handlers) {
    return {
      ...this.handlers,
      ...handlers,
    };
  }
}

const entity = new Entity();
export { Entity, entity, addChildListener, removeChildListeners };